@file:Suppress("CAST_NEVER_SUCCEEDS")

package org.moxultimate.topwithmox.components.crud

import androidx.compose.runtime.*
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.*
import org.w3c.files.File

/**
 * Drag-and-drop / click-to-browse file input zone for new uploads.
 *
 * @param label     Human-readable label shown in the empty state ("Image" / "Thumbnail").
 * @param file      Currently selected file, or null when nothing is selected yet.
 * @param fullWidth When true, stretches the zone to 100% of its parent instead of 300 px.
 * @param onFileDropped Called with the selected/dropped file.
 * @param onFileRemoved Called when the user clicks the remove badge.
 */
@Composable
fun CRUDDropZone(
    label: String,
    file: File?,
    fullWidth: Boolean = false,
    onFileDropped: (File) -> Unit,
    onFileRemoved: () -> Unit,
) {
    var isDragOver by remember { mutableStateOf(false) }
    var previewUrl by remember { mutableStateOf<String?>(null) }

    // Keep callback fresh without re-registering native listeners
    val callbackHolder = remember { object { var fn: (File) -> Unit = {} } }
    callbackHolder.fn = onFileDropped

    LaunchedEffect(file) {
        previewUrl?.let { kotlinx.browser.window.asDynamic().URL.revokeObjectURL(it) }
        previewUrl = file?.let { kotlinx.browser.window.asDynamic().URL.createObjectURL(it) as String }
    }

    fun openFilePicker() {
        val input = kotlinx.browser.document.createElement("input")
        input.setAttribute("type", "file")
        input.setAttribute("accept", "image/*")
        input.asDynamic().onchange = { event: dynamic ->
            val picked = event.target.files[0] as? File
            if (picked != null) callbackHolder.fn(picked)
            Unit
        }
        input.asDynamic().click()
    }

    Div(
        attrs = {
            style {
                property("width", if (fullWidth) "100%" else "300px")
                property("min-height", if (fullWidth) "200px" else "140px")
                property("border", if (isDragOver) "2px dashed #f47b20" else "2px dashed #d0d0d0")
                property("border-radius", "12px")
                property("display", "flex")
                property("flex-direction", "column")
                property("align-items", "center")
                property("justify-content", "center")
                property("gap", "8px")
                property("padding", "16px")
                property("cursor", "pointer")
                property("overflow", "hidden")
                property("position", "relative")
                property("transition", "border-color 0.2s ease, background-color 0.2s ease")
                property("background-color", if (isDragOver) "#fff8f4" else "#fafafa")
                property("box-sizing", "border-box")
            }
            // Use ref{} to attach native DOM listeners — bypasses Compose HTML wrapper issues
            ref { element ->
                val onDragOver: (dynamic) -> Unit = { e ->
                    e.preventDefault()
                    isDragOver = true
                }
                val onDragLeave: (dynamic) -> Unit = { _ ->
                    isDragOver = false
                }
                val onDrop: (dynamic) -> Unit = { e ->
                    e.preventDefault()
                    isDragOver = false
                    val droppedFile = e.dataTransfer.files.item(0) as? File
                    if (droppedFile != null) callbackHolder.fn(droppedFile)
                }
                element.addEventListener("dragover", onDragOver)
                element.addEventListener("dragleave", onDragLeave)
                element.addEventListener("drop", onDrop)
                onDispose {
                    element.removeEventListener("dragover", onDragOver)
                    element.removeEventListener("dragleave", onDragLeave)
                    element.removeEventListener("drop", onDrop)
                }
            }
            onClick { if (file == null) openFilePicker() }
        }
    ) {
        if (previewUrl != null) {
            // Preview image: 300px wide, height adapts to aspect ratio (max 300px)
            Img(
                src = previewUrl!!,
                attrs = {
                    style {
                        property("width", "100%")
                        property("max-height", "300px")
                        property("object-fit", "contain")
                        property("border-radius", "10px")
                        property("pointer-events", "none")
                        property("display", "block")
                    }
                }
            )
            Span(attrs = {
                style {
                    property("margin-top", "8px")
                    property("display", "inline-flex")
                    property("align-items", "center")
                    property("gap", "4px")
                    property("padding", "4px 10px")
                    property("background-color", "#ffe5e5")
                    property("border-radius", "8px")
                    property("font-family", "sans-serif")
                    property("font-size", "12px")
                    property("font-weight", "600")
                    property("color", "#cc3333")
                    property("cursor", "pointer")
                }
                onClick { event ->
                    event.stopPropagation()
                    onFileRemoved()
                }
            }) {
                Text("✕ Remove")
            }
        } else {
            Span(attrs = {
                style {
                    property("font-size", "28px")
                    property("pointer-events", "none")
                }
            }) {
                Text(if (isDragOver) "📂" else "⬆")
            }
            Span(attrs = {
                style {
                    property("font-family", "sans-serif")
                    property("font-size", "13px")
                    property("color", if (isDragOver) "#f47b20" else "#666")
                    property("text-align", "center")
                    property("pointer-events", "none")
                }
            }) {
                Text(
                    if (isDragOver) "Release to drop" else "Drop $label here\nor click to browse"
                )
            }
        }
    }
}

/**
 * Drop zone that shows the existing server image by default.
 * When the user selects a new file, shows a preview with a "Remove new" badge.
 *
 * @param label       Human-readable slot name ("Image" / "Thumbnail").
 * @param existingUrl URL of the currently stored image (shown until replaced).
 * @param file        Newly selected local file, or null when not yet changed.
 * @param minHeight   Minimum height of the drop zone in px. Defaults to 160.
 * @param onFileDropped Called with the new file when the user drops or browses.
 * @param onFileRemoved Called when the user discards the newly selected file.
 */
@Composable
fun CRUDExistingDropZone(
    label: String,
    existingUrl: String,
    file: File?,
    minHeight: Int = 160,
    onFileDropped: (File) -> Unit,
    onFileRemoved: () -> Unit,
) {
    var isDragOver by remember { mutableStateOf(false) }
    var newFilePreviewUrl by remember { mutableStateOf<String?>(null) }

    val callbackHolder = remember { object { var fn: (File) -> Unit = {} } }
    callbackHolder.fn = onFileDropped

    LaunchedEffect(file) {
        newFilePreviewUrl?.let { kotlinx.browser.window.asDynamic().URL.revokeObjectURL(it) }
        newFilePreviewUrl = file?.let { kotlinx.browser.window.asDynamic().URL.createObjectURL(it) as String }
    }

    val displayUrl = newFilePreviewUrl ?: existingUrl.takeIf { it.isNotBlank() }

    fun openFilePicker() {
        val input = kotlinx.browser.document.createElement("input")
        input.setAttribute("type", "file")
        input.setAttribute("accept", "image/*")
        input.asDynamic().onchange = { event: dynamic ->
            val picked = event.target.files[0] as? File
            if (picked != null) callbackHolder.fn(picked)
            Unit
        }
        input.asDynamic().click()
    }

    Div(
        attrs = {
            style {
                property("width", "100%")
                property("min-height", "${minHeight}px")
                property("border", if (isDragOver) "2px dashed #f47b20" else "2px dashed #d0d0d0")
                property("border-radius", "12px")
                property("display", "flex")
                property("flex-direction", "column")
                property("align-items", "center")
                property("justify-content", "center")
                property("gap", "8px")
                property("padding", "16px")
                property("cursor", "pointer")
                property("overflow", "hidden")
                property("position", "relative")
                property("transition", "border-color 0.2s ease, background-color 0.2s ease")
                property("background-color", if (isDragOver) "#fff8f4" else "#fafafa")
                property("box-sizing", "border-box")
            }
            ref { element ->
                val onDragOver: (dynamic) -> Unit = { e -> e.preventDefault(); isDragOver = true }
                val onDragLeave: (dynamic) -> Unit = { _ -> isDragOver = false }
                val onDrop: (dynamic) -> Unit = { e ->
                    e.preventDefault(); isDragOver = false
                    val f = e.dataTransfer.files.item(0) as? File
                    if (f != null) callbackHolder.fn(f)
                }
                element.addEventListener("dragover", onDragOver)
                element.addEventListener("dragleave", onDragLeave)
                element.addEventListener("drop", onDrop)
                onDispose {
                    element.removeEventListener("dragover", onDragOver)
                    element.removeEventListener("dragleave", onDragLeave)
                    element.removeEventListener("drop", onDrop)
                }
            }
            onClick { openFilePicker() }
        }
    ) {
        if (displayUrl != null) {
            Img(
                src = displayUrl,
                attrs = {
                    style {
                        property("width", "100%")
                        property("max-height", "280px")
                        property("object-fit", "contain")
                        property("border-radius", "10px")
                        property("pointer-events", "none")
                        property("display", "block")
                    }
                }
            )
            // Badge: "New" for a freshly-selected file, "Replace" for the existing URL
            Span(attrs = {
                style {
                    property("margin-top", "6px")
                    property("display", "inline-flex")
                    property("align-items", "center")
                    property("gap", "4px")
                    property("padding", "4px 10px")
                    property("border-radius", "8px")
                    property("font-family", "sans-serif")
                    property("font-size", "12px")
                    property("font-weight", "600")
                    property("cursor", "pointer")
                    if (file != null) {
                        property("background-color", "#ffe5e5")
                        property("color", "#cc3333")
                    } else {
                        property("background-color", "#e8f4ff")
                        property("color", "#1a6fa8")
                    }
                }
                onClick { event ->
                    event.stopPropagation()
                    if (file != null) onFileRemoved() else openFilePicker()
                }
            }) {
                Text(if (file != null) "✕ Remove new" else "↑ Replace $label")
            }
        } else {
            Span(attrs = { style { property("font-size", "28px"); property("pointer-events", "none") } }) {
                Text(if (isDragOver) "📂" else "⬆")
            }
            Span(attrs = {
                style {
                    property("font-family", "sans-serif")
                    property("font-size", "13px")
                    property("color", if (isDragOver) "#f47b20" else "#666")
                    property("text-align", "center")
                    property("pointer-events", "none")
                }
            }) {
                Text(if (isDragOver) "Release to drop" else "Drop new $label here\nor click to browse")
            }
        }
    }
}
